<script lang="ts">
  /**
   * The 3D track replay.
   *
   * What MapLibre 6.4 gives natively, and what it does not:
   *
   *   * **Terrain** — a `raster-dem` source with `terrarium` encoding, switched on with
   *     `setTerrain`. Real relief: the track runs into valleys and over ridges rather than
   *     across a flat plane.
   *   * **Sky** — a native `sky` layer, so a pitched view has a horizon rather than a void.
   *   * **Lines at altitude — it does not.** There is no `line-z-offset` in this version, so a
   *     track cannot simply float at its true height. The answer here is the classic flight
   *     wall: one `fill-extrusion` polygon per track segment, rising from the ground to the
   *     aircraft's height above the take-off field. That reads as altitude at a glance,
   *     works on every device, and needs no custom WebGL layer to maintain.
   *
   * The wall's height is **above the take-off elevation**, not above sea level. Extrusion
   * heights in MapLibre are measured from the terrain surface, so an MSL height would sink
   * into rising ground and float over a valley — and "1200 m above the field" is what a pilot
   * reads off a variometer anyway. The readout beside the map shows MSL, and says so.
   */
  // MapLibre 6 has no default export — only named ones. `Map` is aliased because the global
  // `Map` is very much still in scope inside a component.
  import { LngLat, Map as MapLibreMap, NavigationControl, ScaleControl } from 'maplibre-gl';
  import type { GeoJSONSource, LngLatBoundsLike, StyleSpecification } from 'maplibre-gl';
  import type { Feature, FeatureCollection } from 'geojson';
  import 'maplibre-gl/dist/maplibre-gl.css';
  import AirspaceControls from './AirspaceControls.svelte';
  import LoopingPlane from './LoopingPlane.svelte';
  import { collapseAttribution } from './maplibre.ts';
  import { skyFor } from './sky.ts';
  import type { SkyWeather } from './sky.ts';
  import {
    AIRSPACE_GROUPS,
    AIRSPACE_MAX_ZOOM,
    AIRSPACE_SOURCE,
    AIRPORT_FILTER,
    MAJOR_AIRPORT_FILTER,
    MINOR_AIRPORT_FILTER,
    MINOR_AIRPORT_MIN_ZOOM,
    AIRPORT_SOURCE_LAYER,
    AIRSPACE_SOURCE_LAYER,
    airspaceColour,
    DEFAULT_AIRSPACE_GROUPS,
    enabledFilter,
    limitToMetres,
  } from './airspace.ts';
  import { untrack } from 'svelte';
  import type { Snippet } from 'svelte';
  import { api } from './api.ts';
  import type { MapConfig, NotamIndex, RadarFrame, ThermalIndex, TrackFeature } from './api.ts';

  interface Props {
    track: TrackFeature;
    config: MapConfig;
    /** Index into the track, driven by the scrubber. */
    cursor: number;
    basemap: 'satellite' | 'vector';
    showAirspace: boolean;
    /** The weather at the time of the flight, so the sky is that day's. Absent means clear. */
    weather?: SkyWeather | undefined;
    /** Take-off field name for the map label. Null when the flight started outside coverage. */
    takeoffPlace?: string | null | undefined;
    /** Landing field name. Null for a flight still in the air — there is nothing to label yet. */
    landingPlace?: string | null | undefined;
    /**
     * Extra chrome for the top-left stack, under the layer switcher.
     *
     * A snippet rather than a prop of its own, because the map should not have to know what a
     * GAFOR outlook is — it only knows where things go on top of it. Whoever has the data owns
     * the markup; the map owns the position.
     */
    chrome?: Snippet | undefined;
    /**
     * Offer the precipitation radar overlay.
     *
     * Off for a flight that has already landed, and that is a correctness point rather than a
     * simplification: the radar only has the last two hours, so on a replay of last Thursday it
     * would draw today's showers over it. The sky dome already carries that day's weather.
     */
    offerRadar?: boolean | undefined;
    /**
     * Offer the thermal history overlay.
     *
     * For a glider in the air. Pointless for the ultralight — it does not use thermals — and
     * for a landed flight the question it answers has already been answered by flying it.
     */
    offerThermals?: boolean | undefined;
    /**
     * NOTAMs to draw, or null when there are none to offer.
     *
     * Passed in rather than fetched here because whether the layer exists at all depends on
     * whether the daemon has FAA credentials, and that answer arrives with the data.
     */
    notams?: NotamIndex | null | undefined;
    /**
     * Cockpit view: the camera sits at the aircraft and looks where it is going.
     *
     * Bindable, because the button that turns it on lives beside the play control rather than
     * in the layer switcher — it is a way of watching the replay, not a layer.
     */
    cockpit?: boolean;
  }

  let {
    track,
    config,
    cursor,
    basemap = $bindable('satellite'),
    showAirspace = $bindable(false),
    weather = {},
    takeoffPlace = null,
    landingPlace = null,
    chrome = undefined,
    offerRadar = false,
    offerThermals = false,
    notams = null,
    cockpit = $bindable(false),
  }: Props = $props();

  let container: HTMLDivElement;
  let map: MapLibreMap | undefined;
  let ready = $state(false);
  /** Which classes of airspace are drawn, and how strongly. Same control as the heatmap. */
  let airGroups = $state(new Set(DEFAULT_AIRSPACE_GROUPS));
  let airOpacity = $state(0.12);
  let showRadar = $state(false);
  let radarFrames = $state<readonly RadarFrame[]>([]);
  /** Which frame is on screen. The newest observation by default, never the nowcast. */
  let radarFrame = $state<number | null>(null);
  let showThermals = $state(false);
  let thermals = $state<ThermalIndex | null>(null);
  let showNotams = $state(false);
  let failure = $state<string | null>(null);
  let stalled = $state(false);

  /**
   * How long to wait before saying something rather than showing a spinner forever.
   *
   * MapLibre's `load` fires only after the first *render*, and the render loop is
   * `requestAnimationFrame`. In a tab the browser is not compositing — a background tab, a
   * headless pane, a window scrolled entirely out of view — rAF never fires, so the style
   * loads, the tiles are never requested, and `load` never arrives. That looks exactly like a
   * broken map and is not one, so it gets its own message.
   */
  const STALL_TIMEOUT_MS = 15_000;

  const TRACK_SOURCE = 'flight-track';
  const WALL_SOURCE = 'flight-wall';
  const GROUND_SOURCE = 'flight-ground';
  const MARKER_SOURCE = 'flight-marker';
  const TERRAIN_SOURCE = 'terrain-dem';
  const ENDS_SOURCE = 'flight-ends';
  const RADAR_SOURCE = 'rain-radar';
  const THERMAL_SOURCE = 'thermals';
  const NOTAM_SOURCE = 'notams';

  const hasLayer = (id: string) =>
    config.layers.some((l) => l.id === id);

  const tileUrl = (layer: string) => config.tileUrlTemplate.replace('{layer}', layer);
  const orthoLayer = $derived(config.layers.find((l) => l.id === 'ortho'));

  /** Ground elevation at the take-off point, which the wall is measured from. */
  const baseAltitude = () => {
    for (const alt of track.properties.altitudes) if (alt !== null) return alt;
    return 0;
  };

  /**
   * The two ends of the flight, as labelled points.
   *
   * Take-off and landing are the first thing anybody asks of a logbook entry and the map was
   * the one place that did not answer it — a track with two ends and no way to tell which was
   * which, least of all on a there-and-back where they sit on top of each other.
   *
   * The landing point is only produced once there is one. A flight in the air has a most recent
   * fix, and labelling that "Landung" would be a straightforward lie.
   */
  function endpointFeatures(): FeatureCollection {
    const coords = track.geometry.coordinates;
    const first = coords[0];
    const last = coords[coords.length - 1];
    const features: Feature[] = [];

    if (first !== undefined) {
      features.push({
        type: 'Feature',
        properties: {
          kind: 'start',
          label: takeoffPlace === null ? 'Start' : `Start · ${takeoffPlace}`,
        },
        geometry: { type: 'Point', coordinates: [first[0] as number, first[1] as number] },
      });
    }
    if (landingPlace !== null && last !== undefined && coords.length > 1) {
      features.push({
        type: 'Feature',
        properties: { kind: 'landing', label: `Landung · ${landingPlace}` },
        geometry: { type: 'Point', coordinates: [last[0] as number, last[1] as number] },
      });
    }
    return { type: 'FeatureCollection', features };
  }

  /**
   * One extruded quad per segment, coloured by climb rate.
   *
   * A polygon per segment rather than one long one: `fill-extrusion-height` is a per-feature
   * property, so a single polygon could only have a single height — which is exactly the
   * information the wall exists to show.
   */
  function buildWall(): FeatureCollection {
    const coords = track.geometry.coordinates;
    const alts = track.properties.altitudes;
    const times = track.properties.times;
    const base = baseAltitude();

    const features: Feature[] = [];

    for (let i = 0; i + 1 < coords.length; i++) {
      const a = coords[i];
      const b = coords[i + 1];
      if (a === undefined || b === undefined) continue;

      const altA = alts[i];
      const altB = alts[i + 1];
      if (altA === null || altA === undefined || altB === null || altB === undefined) continue;

      const dt = (times[i + 1] ?? 0) - (times[i] ?? 0);
      const climb = dt > 0 ? (altB - altA) / dt : 0;

      // A ribbon with real width, and that is the whole fix.
      //
      // This used to be `[A, B, B, A]` — a ring enclosing zero area — on the belief that
      // MapLibre would extrude its outline into a clean vertical sheet. It does not: a
      // fill-extrusion is triangulated, a degenerate ring triangulates to nothing, and the
      // track simply never appeared on any map. Twenty-four metres wide is invisible from
      // above and a solid wall the moment the camera tilts.
      const midLat = ((a[1] + b[1]) / 2) * (Math.PI / 180);
      const dx = (b[0] - a[0]) * Math.cos(midLat);
      const dy = b[1] - a[1];
      const len = Math.hypot(dx, dy);
      // Degenerate segments do happen — two fixes at the same place while circling on the
      // ground — and normalising by zero would put NaN into the geometry, which MapLibre
      // discards along with everything after it in the same source.
      if (len === 0) continue;
      const halfWidthDeg = 12 / 111_320;
      const ox = ((-dy / len) * halfWidthDeg) / Math.cos(midLat);
      const oy = (dx / len) * halfWidthDeg;

      features.push({
        type: 'Feature',
        properties: {
          height: Math.max(0, (altA + altB) / 2 - base),
          climb,
          index: i,
        },
        geometry: {
          type: 'Polygon',
          coordinates: [
            [
              [a[0] + ox, a[1] + oy],
              [b[0] + ox, b[1] + oy],
              [b[0] - ox, b[1] - oy],
              [a[0] - ox, a[1] - oy],
              [a[0] + ox, a[1] + oy],
            ],
          ],
        },
      });
    }

    return { type: 'FeatureCollection', features };
  }

  function markerFeature(index: number): FeatureCollection {
    const point = track.geometry.coordinates[Math.max(0, Math.min(index, track.geometry.coordinates.length - 1))];
    if (point === undefined) return { type: 'FeatureCollection', features: [] };
    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: { type: 'Point', coordinates: [point[0], point[1]] },
        },
      ],
    };
  }

  function bounds(): LngLatBoundsLike {
    let west = 180;
    let south = 90;
    let east = -180;
    let north = -90;
    for (const [lon, lat] of track.geometry.coordinates) {
      west = Math.min(west, lon);
      east = Math.max(east, lon);
      south = Math.min(south, lat);
      north = Math.max(north, lat);
    }
    return [
      [west, south],
      [east, north],
    ];
  }

  function styleFor(kind: 'satellite' | 'vector'): StyleSpecification | string {
    if (kind === 'vector') {
      // The only thing the browser fetches directly: a style document, keyless, no credential
      // in it. Every actual tile still comes through the daemon's proxy.
      return config.basemapStyleUrl;
    }

    return {
      version: 8,
      // OpenFreeMap hosts the glyphs the vector style uses; the satellite style needs its own
      // set for the few labels it draws, and this is the same keyless host.
      glyphs: config.basemapGlyphsUrl,
      sources: {
        satellite: {
          type: 'raster',
          tiles: [tileUrl('satellite')],
          tileSize: 256,
          maxzoom: 14,
          attribution:
            config.layers.find((l) => l.id === 'satellite')?.attribution ?? '',
        },
        // 20 cm orthophotos over Baden-Wuerttemberg, on top of the 10 m global mosaic. The
        // bounds come from the layer catalogue: outside the state the service answers 400, so
        // this must not be asked, and Sentinel-2 shows through there instead.
        ...(orthoLayer === undefined
          ? {}
          : {
              ortho: {
                type: 'raster' as const,
                tiles: [tileUrl('ortho')],
                tileSize: 256,
                maxzoom: 20,
                // Below this the state boundary is in shot and the imagery ends in a straight
                // line across the map, which reads as a rendering fault. Zoomed in you are
                // inside Baden-Wuerttemberg and never see an edge at all.
                minzoom: 9,
                ...(orthoLayer.bounds === null ? {} : { bounds: orthoLayer.bounds }),
                attribution: orthoLayer.attribution,
              },
            }),
      },
      layers: [
        { id: 'background', type: 'background', paint: { 'background-color': '#0d0f14' } },
        { id: 'satellite', type: 'raster', source: 'satellite' },
        ...(orthoLayer === undefined
          ? []
          : [
              {
                id: 'ortho',
                type: 'raster' as const,
                source: 'ortho',
                // Faded in over two zoom levels rather than switched on. The two sources have
                // different colour balance and different sun angles, so a hard swap looks like
                // a seam; a cross-fade reads as the picture simply getting sharper.
                paint: {
                  'raster-opacity': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    9,
                    0,
                    11.5,
                    1,
                  ] as unknown as number,
                },
              },
            ]),
      ],
    } satisfies StyleSpecification;
  }

  /**
   * Airfields, always on and independent of the airspace toggle.
   *
   * They travel in the same openAIP tile as the airspace, so they cost no extra request — and
   * a soaring map without the fields on it is missing the thing every decision is made against.
   * A gliding site gets the accent colour; everything else is neutral, because at a glance the
   * question is "can I get in there" and the answer is different for the two.
   */
  function addAirports(m: MapLibreMap): void {
    for (const id of [
      `${AIRSPACE_SOURCE}-ap-major`,
      `${AIRSPACE_SOURCE}-ap`,
      `${AIRSPACE_SOURCE}-ap-label-major`,
      `${AIRSPACE_SOURCE}-ap-label`,
    ]) {
      if (m.getLayer(id) !== undefined) m.removeLayer(id);
    }
    if (m.getSource(AIRSPACE_SOURCE) === undefined) return;

    const dot = (id: string, filter: typeof AIRPORT_FILTER, minzoom?: number) => {
      m.addLayer({
        id,
        type: 'circle',
        source: AIRSPACE_SOURCE,
        'source-layer': AIRPORT_SOURCE_LAYER,
        filter,
        ...(minzoom === undefined ? {} : { minzoom }),
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 7, 2.8, 12, 5.5],
          // The club's own kind of field in the accent colour: at a glance the question is
          // "can I get in there", and for a gliding site the answer is different.
          'circle-color': ['case', ['==', ['get', 'type'], 'gliding'], '#f7cc4a', '#cfd6e0'],
          'circle-stroke-color': 'rgba(0,0,0,0.6)',
          'circle-stroke-width': 1,
        },
      });
    };

    const label = (id: string, filter: typeof AIRPORT_FILTER, minzoom: number) => {
      m.addLayer({
        id,
        type: 'symbol',
        source: AIRSPACE_SOURCE,
        'source-layer': AIRPORT_SOURCE_LAYER,
        filter,
        minzoom,
        layout: {
          'text-field': ['get', 'name'],
          'text-font': ['Noto Sans Regular'],
          'text-size': ['interpolate', ['linear'], ['zoom'], 6, 9, 9, 10, 13, 13],
          'text-offset': [0, 0.9],
          'text-anchor': 'top',
          'text-allow-overlap': false,
        },
        paint: {
          'text-color': '#e7ecf3',
          'text-halo-color': 'rgba(0,0,0,0.75)',
          'text-halo-width': 1.4,
        },
      });
    };

    // Commercial airports at every zoom — they are what anyone orients by on a country-wide
    // view. Everything else only once there is room for it: four thousand airfields at zoom 6
    // is a grey mat with no information in it.
    dot(`${AIRSPACE_SOURCE}-ap-major`, MAJOR_AIRPORT_FILTER);
    dot(`${AIRSPACE_SOURCE}-ap`, MINOR_AIRPORT_FILTER, MINOR_AIRPORT_MIN_ZOOM);
    label(`${AIRSPACE_SOURCE}-ap-label-major`, MAJOR_AIRPORT_FILTER, 6);
    label(`${AIRSPACE_SOURCE}-ap-label`, MINOR_AIRPORT_FILTER, MINOR_AIRPORT_MIN_ZOOM);
  }

  /**
   * How far ahead along the track the cockpit camera takes its *direction* from.
   *
   * Metres of *track*, not of straight-line distance, so the view behaves the same whether the
   * aircraft is gliding at 120 km/h or grinding round a thermal. Six hundred is a little under
   * one turn of a circle: far enough that GPS jitter does not shake the horizon, close enough
   * that a turn still reads as a turn rather than as a slow drift.
   */
  const COCKPIT_LOOKAHEAD_M = 600;

  /**
   * How far away the point the camera actually looks *at* is, and how far below the aircraft.
   *
   * These are not the same thing as the look-ahead above, and conflating them was the first
   * version's mistake. `calculateCameraOptionsFromTo` derives the zoom from the distance to the
   * target, so aiming at a point six hundred metres ahead produced a telescope: zoom 16, a
   * one-metre scale bar, and a screen full of the hillside directly in front. A pilot looks at
   * the horizon, so the target is put fourteen kilometres out along the direction the track is
   * going — which gives a wide field and a real horizon.
   *
   * The drop tips the nose down by about four and a half degrees, so the view holds ground in
   * the lower part of the frame and sky above it rather than staring at the skyline.
   */
  const COCKPIT_TARGET_M = 14_000;
  const COCKPIT_TARGET_DROP_M = 1_100;

  /**
   * Above the 85 the rest of the map uses, so a level flight can look at the horizon.
   *
   * MapLibre calls anything past 60 experimental and it has behaved; the alternative is that
   * `calculateCameraOptionsFromTo` returns 88 degrees, `jumpTo` clamps it to 85, and the camera
   * quietly stops being where the aircraft is.
   */
  const COCKPIT_MAX_PITCH = 95;

  /** Everything drawn at the aircraft's own position, which from inside is drawn on the canopy. */
  const COCKPIT_HIDDEN = ['flight-wall', 'flight-marker', 'flight-marker-halo'];

  /**
   * Relief is exaggerated for the overview and must not be for the cockpit.
   *
   * 1.35 makes the Alb read as hills from above, which is the point. Inside the aircraft it is
   * a contradiction: the altitudes are real and the ground is 35 % too high, so at 200 m above
   * a 700 m ridge the camera ends up **inside the hill** — MapLibre renders that as a screen of
   * vertical smears, which is exactly what the first attempt produced.
   */
  const TERRAIN_EXAGGERATION = 1.35;

  const METRES_PER_DEGREE = 111_320;

  /**
   * Where the camera goes for the fix at `index`, or null if it cannot be worked out.
   *
   * `calculateCameraOptionsFromTo` is MapLibre 6's own answer to "put the camera here, pointed
   * at there" — it returns the centre, zoom, bearing and pitch that produce exactly that view,
   * so none of this has to be derived by hand.
   */
  function cockpitCamera(index: number): ReturnType<MapLibreMap['calculateCameraOptionsFromTo']> | null {
    const m = map;
    const coords = track.geometry.coordinates;
    const alts = track.properties.altitudes;
    const here = coords[index];
    if (m === undefined || here === undefined) return null;

    const altAt = (i: number): number => {
      // Walk back for the last known altitude: a single null fix must not drop the camera to
      // sea level for one frame, which reads as the aircraft falling through the terrain.
      for (let j = i; j >= 0; j--) {
        const a = alts[j];
        if (a !== null && a !== undefined) return a;
      }
      return baseAltitude();
    };

    const cosLat = Math.cos((here[1] * Math.PI) / 180);

    // Walk forward until enough track has passed under the aircraft, to get a direction that is
    // the aircraft's own rather than one fix's worth of GPS noise. Equirectangular distance:
    // this never spans more than a kilometre, where the error is centimetres.
    let travelled = 0;
    let ahead = index;
    for (let i = index + 1; i < coords.length; i++) {
      const a = coords[i - 1];
      const b = coords[i];
      if (a === undefined || b === undefined) continue;
      travelled += Math.hypot((b[0] - a[0]) * cosLat * METRES_PER_DEGREE, (b[1] - a[1]) * METRES_PER_DEGREE);
      ahead = i;
      if (travelled >= COCKPIT_LOOKAHEAD_M) break;
    }

    // At the very end of the track there is nothing ahead, so the direction comes from behind.
    const reference = ahead === index ? coords[Math.max(0, index - 1)] : coords[ahead];
    if (reference === undefined) return null;
    const dx = (ahead === index ? here[0] - reference[0] : reference[0] - here[0]) * cosLat;
    const dy = ahead === index ? here[1] - reference[1] : reference[1] - here[1];
    const length = Math.hypot(dx, dy);
    if (length === 0) return null;

    // The look-at point: far out along that direction, and a little below.
    const reach = COCKPIT_TARGET_M / METRES_PER_DEGREE;
    const target: [number, number] = [
      here[0] + ((dx / length) * reach) / cosLat,
      here[1] + (dy / length) * reach,
    ];

    return m.calculateCameraOptionsFromTo(
      new LngLat(here[0], here[1]),
      altAt(index),
      new LngLat(target[0], target[1]),
      altAt(index) - COCKPIT_TARGET_DROP_M,
    );
  }


  /**
   * Drive the camera from the scrubber while the cockpit is on.
   *
   * `jumpTo` and not `easeTo`: the cursor already moves smoothly, and easing on top of it adds a
   * second, slower animation that lags the aircraft and makes the horizon swim.
   */
  $effect(() => {
    const m = map;
    const index = cursor;
    if (m === undefined || !ready || !cockpit) return;
    const camera = cockpitCamera(index);
    if (camera !== null) m.jumpTo(camera);
  });

  /** Entering and leaving the mode: pitch ceiling, ground clamping, and the view to return to. */
  $effect(() => {
    const m = map;
    const on = cockpit;
    if (m === undefined || !ready) return;

    if (on) {
      m.setMaxPitch(COCKPIT_MAX_PITCH);
      // True relief, or the aircraft flies through the scenery. See TERRAIN_EXAGGERATION.
      if (hasLayer('terrain')) m.setTerrain({ source: TERRAIN_SOURCE, exaggeration: 1 });
      // Without this MapLibre pins the centre to the terrain and the camera is dragged down to
      // the ground however high the aircraft is.
      m.setCenterClampedToGround(false);
      /*
       * Three layers have to go, all for the same reason: the camera is *on* the track, and
       * anything drawn at the track is therefore drawn on the canopy.
       *
       * The wall is the striking one. It rises from the ground to the aircraft's height along
       * the whole flight, so from inside it the view is a solid sheet of climb-rate orange —
       * the first cockpit frame was an entirely brown screen with a horizon somewhere behind
       * it. The marker and its halo are screen-space circles at the camera's own position, so
       * they sit as a blob in the middle of the view.
       *
       * The ground track stays. A thin line running away towards where you are going is
       * exactly what you want to see out of the front.
       */
      for (const id of COCKPIT_HIDDEN) {
        if (m.getLayer(id) !== undefined) m.setLayoutProperty(id, 'visibility', 'none');
      }
      return () => {
        m.setCenterClampedToGround(true);
        m.setMaxPitch(85);
        if (hasLayer('terrain')) {
          m.setTerrain({ source: TERRAIN_SOURCE, exaggeration: TERRAIN_EXAGGERATION });
        }
        for (const id of COCKPIT_HIDDEN) {
          if (m.getLayer(id) !== undefined) m.setLayoutProperty(id, 'visibility', 'visible');
        }
        // Back to the view the page opened on, rather than leaving the reader inside the
        // cockpit's near-vertical camera with no obvious way out.
        resetView();
      };
    }
    return;
  });

  /**
   * The sky, re-derived whenever the camera is laid over far enough to matter.
   *
   * Rounded to five degrees before comparing: `setSky` rebuilds the dome, and doing that on
   * every frame of a drag would be a lot of work for a colour nobody can see changing. Five
   * degrees is below the threshold of noticing and above the noise of a trackpad.
   */
  let skyPitch = -1;
  function applySky(m: MapLibreMap): void {
    const rounded = Math.round(m.getPitch() / 5) * 5;
    if (rounded === skyPitch) return;
    skyPitch = rounded;
    m.setSky(skyFor(weather, rounded));
  }

  /** Everything that has to be re-added after a style change, which wipes sources and layers. */
  function addTrackLayers(m: MapLibreMap): void {
    if (hasLayer('terrain')) {
      m.addSource(TERRAIN_SOURCE, {
        type: 'raster-dem',
        tiles: [tileUrl('terrain')],
        tileSize: 256,
        maxzoom: 13,
        // Terrarium, not Mapbox RGB: the AWS Open Data tiles use that encoding, and reading
        // them as RGB produces a landscape of absurd spikes.
        encoding: 'terrarium',
      });
      m.setTerrain({ source: TERRAIN_SOURCE, exaggeration: TERRAIN_EXAGGERATION });
    }

    applySky(m);

    // Airspace as volumes, from the vector tiles: the raster layer could show a boundary but
    // never a floor, and on a 3D replay the question is always "was I under it".
    // The source carries the airfields as well as the airspace, so it goes on regardless of the
    // toggle — otherwise switching airspace off would take every field off the map with it.
    if (hasLayer('airspace') && m.getSource(AIRSPACE_SOURCE) === undefined) {
      m.addSource(AIRSPACE_SOURCE, {
        type: 'vector',
        tiles: [tileUrl('airspace')],
        maxzoom: AIRSPACE_MAX_ZOOM,
      });
    }
    if (hasLayer('airspace')) {
      const filter = enabledFilter(airGroups);
      const colour = airspaceColour(AIRSPACE_GROUPS);
      // Added regardless and hidden with `visibility`, so the toggle is a layout property flip
      // rather than a rebuild — and so the effect that owns the toggle always has something to
      // flip.
      const visibility = showAirspace ? 'visible' : ('none' as const);
      m.addLayer({
        id: `${AIRSPACE_SOURCE}-fill`,
        type: 'fill-extrusion',
        source: AIRSPACE_SOURCE,
        'source-layer': AIRSPACE_SOURCE_LAYER,
        filter,
        layout: { visibility },
        paint: {
          'fill-extrusion-base': limitToMetres('lower', baseAltitude()),
          'fill-extrusion-height': limitToMetres('upper', baseAltitude()),
          'fill-extrusion-color': colour,
          // Lower than the heatmap's by default, because the flight wall has to stay readable
          // through it — but the reader can say otherwise.
          'fill-extrusion-opacity': airOpacity,
        },
      });
      m.addLayer({
        id: `${AIRSPACE_SOURCE}-line`,
        type: 'line',
        source: AIRSPACE_SOURCE,
        'source-layer': AIRSPACE_SOURCE_LAYER,
        filter,
        layout: { visibility },
        paint: { 'line-color': colour, 'line-width': 1, 'line-opacity': 0.7 },
      });
    }
    addAirports(m);

    // The ground track, drawn flat and always.
    //
    // The extruded wall shows altitude and needs a tilted camera to read; looking straight down
    // it is a hairline. This is the plain answer to "where did it go", it survives any camera,
    // and it does not depend on the extrusion working at all — which for a while it did not.
    m.addSource(GROUND_SOURCE, {
      type: 'geojson',
      data: { type: 'Feature', properties: {}, geometry: track.geometry },
    });
    m.addLayer({
      id: 'flight-ground',
      type: 'line',
      source: GROUND_SOURCE,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': '#f7cc4a',
        'line-width': ['interpolate', ['linear'], ['zoom'], 6, 1.4, 12, 3],
        'line-opacity': 0.9,
      },
    });

    // Take-off and landing, on top of the track so they survive a zoomed-out view where the
    // two ends of a local flight are the same pixel.
    m.addSource(ENDS_SOURCE, { type: 'geojson', data: endpointFeatures() });
    m.addLayer({
      id: 'flight-ends',
      type: 'circle',
      source: ENDS_SOURCE,
      paint: {
        'circle-radius': 5.5,
        'circle-color': ['case', ['==', ['get', 'kind'], 'start'], '#5ad18f', '#ff6b6b'],
        'circle-stroke-color': '#0d0f14',
        'circle-stroke-width': 2,
      },
    });
    m.addLayer({
      id: 'flight-ends-label',
      type: 'symbol',
      source: ENDS_SOURCE,
      layout: {
        'text-field': ['get', 'label'],
        'text-font': ['Noto Sans Regular'],
        'text-size': 11,
        // Start above, landing below: on a there-and-back the two points coincide and one
        // offset would stack the labels exactly on top of each other.
        'text-offset': ['case', ['==', ['get', 'kind'], 'start'], ['literal', [0, -1.1]], ['literal', [0, 1.1]]],
        'text-anchor': ['case', ['==', ['get', 'kind'], 'start'], 'bottom', 'top'],
        'text-allow-overlap': true,
      },
      paint: {
        'text-color': '#e7ecf3',
        'text-halo-color': 'rgba(0,0,0,0.8)',
        'text-halo-width': 1.6,
      },
    });

    m.addSource(WALL_SOURCE, { type: 'geojson', data: buildWall() });
    m.addLayer({
      id: 'flight-wall',
      type: 'fill-extrusion',
      source: WALL_SOURCE,
      paint: {
        'fill-extrusion-height': ['get', 'height'],
        'fill-extrusion-base': 0,
        'fill-extrusion-opacity': 0.55,
        // Blue where it is sinking, orange where it is climbing. The colour is the variometer.
        'fill-extrusion-color': [
          'interpolate',
          ['linear'],
          ['get', 'climb'],
          -3,
          '#3f7fd0',
          -0.5,
          '#6f7f9e',
          0.5,
          '#d0762f',
          3,
          '#ff9d3d',
        ],
      },
    });

    m.addSource(TRACK_SOURCE, { type: 'geojson', data: track as unknown as Feature });
    m.addLayer({
      id: 'flight-track',
      type: 'line',
      source: TRACK_SOURCE,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#ff7a3d', 'line-width': 2.5, 'line-opacity': 0.95 },
    });

    m.addSource(MARKER_SOURCE, { type: 'geojson', data: markerFeature(cursor) });
    m.addLayer({
      id: 'flight-marker-halo',
      type: 'circle',
      source: MARKER_SOURCE,
      paint: {
        'circle-radius': 13,
        'circle-color': '#ffb545',
        'circle-opacity': 0.22,
        'circle-blur': 0.4,
      },
    });
    m.addLayer({
      id: 'flight-marker',
      type: 'circle',
      source: MARKER_SOURCE,
      paint: {
        'circle-radius': 6,
        'circle-color': '#ffb545',
        'circle-stroke-color': '#0d0f14',
        'circle-stroke-width': 2,
      },
    });
  }

  $effect(() => {
    // Recreated when the basemap changes: swapping the style wholesale is simpler and more
    // reliable than reconciling two very different source sets, and it happens on a click.
    //
    // **`basemap` is deliberately the only reactive read in here.** `bounds()` walks the track,
    // and a live flight's track is a new object every time it is polled — so tracking it meant
    // tearing the whole map down and building a new one every few seconds, which on a vector
    // style is longer than the interval and the map therefore never finishes loading at all.
    // Everything else has its own effect below and updates the map in place.
    const m = new MapLibreMap({
      container,
      style: styleFor(basemap),
      bounds: untrack(() => bounds()),
      fitBoundsOptions: { padding: 48, pitch: 62, bearing: -20 },
      maxPitch: 85,
      attributionControl: { compact: true },
      // The flight wall is the point of the view; letting it be rotated into is not optional.
      dragRotate: true,
      pitchWithRotate: true,
    });

    map = m;
    ready = false;
    failure = null;
    // A new map means a new sky; without this the cached pitch suppresses the first `setSky`
    // and the style change loses its dome.
    skyPitch = -1;

    // A handle for diagnosing a map that will not load. MapLibre keeps no back-reference from
    // the DOM, so without this there is no way to ask a deployed instance what state it is in —
    // and "the map is grey" is exactly the report that arrives with no other detail.
    (window as unknown as { __flightlogdMap?: MapLibreMap }).__flightlogdMap = m;

    m.addControl(new NavigationControl({ visualizePitch: true }), 'top-right');
    m.addControl(new ScaleControl({ unit: 'metric' }), 'bottom-left');
    collapseAttribution(m);

    // The sky develops as the camera is tilted rather than being fixed at load — see `sky.ts`.
    m.on('pitch', () => {
      if (ready) applySky(m);
    });

    const stallTimer = setTimeout(() => {
      if (!ready && failure === null) stalled = true;
    }, STALL_TIMEOUT_MS);

    m.on('load', () => {
      clearTimeout(stallTimer);
      stalled = false;
      try {
        addTrackLayers(m);
        ready = true;
      } catch (err) {
        failure = err instanceof Error ? err.message : String(err);
      }
    });

    m.on('error', (event) => {
      // A missing tile at the edge of coverage is entirely normal — the satellite mosaic stops
      // at zoom 14, the DEM at 13, and the orthophotos at the state border — and must not blank
      // the map. Anything else must be visible, and for a long time it was not: source errors
      // were logged to the console only, so a basemap that failed to load looked identical to
      // one that was merely slow, and the only evidence was a red badge in somebody's DevTools.
      const sourceId = 'sourceId' in event ? (event.sourceId as string | undefined) : undefined;
      const message = event.error?.message ?? String(event);
      const status = (event.error as { status?: number } | undefined)?.status;
      const missingTile = status === 404 || status === 204 || /404/.test(message);

      console.error('maplibre', message, { sourceId, status });
      if (missingTile) return;
      failure = sourceId === undefined ? message : `${sourceId}: ${message}`;
    });

    return () => {
      clearTimeout(stallTimer);
      map = undefined;
      m.remove();
    };
  });

  const setData = (m: MapLibreMap, id: string, data: Feature | FeatureCollection): void => {
    const source = m.getSource(id);
    if (source !== undefined && 'setData' in source) (source as GeoJSONSource).setData(data);
  };

  // Move the marker without touching anything else — this runs on every scrubber tick.
  $effect(() => {
    const m = map;
    if (m === undefined || !ready) return;
    setData(m, MARKER_SOURCE, markerFeature(cursor));
  });

  /**
   * The track grew — a live flight being polled — so push the new geometry into the sources
   * that already exist rather than rebuilding the map around it.
   *
   * This is the other half of keeping `bounds()` out of the creation effect. The camera is
   * deliberately left alone: re-fitting on every poll would yank the view out from under
   * anybody looking at the map.
   */
  $effect(() => {
    const m = map;
    // Read first so the effect subscribes to the track even when the map is not ready yet.
    const geometry = track.geometry;
    if (m === undefined || !ready) return;
    setData(m, GROUND_SOURCE, { type: 'Feature', properties: {}, geometry });
    setData(m, WALL_SOURCE, buildWall());
    setData(m, TRACK_SOURCE, track as unknown as Feature);
    setData(m, ENDS_SOURCE, endpointFeatures());
  });

  /**
   * Airspace on and off, without a style reload.
   *
   * This used to be read only inside `addTrackLayers`, which runs once from the `load` handler.
   * Svelte tracks synchronous reads, so nothing subscribed to the toggle and the button did
   * precisely nothing until the map happened to be rebuilt for some other reason.
   */
  $effect(() => {
    const m = map;
    const wanted = showAirspace;
    const filter = enabledFilter(airGroups);
    const opacity = airOpacity;
    if (m === undefined || !ready || !hasLayer('airspace')) return;

    for (const id of [`${AIRSPACE_SOURCE}-fill`, `${AIRSPACE_SOURCE}-line`]) {
      if (m.getLayer(id) === undefined) continue;
      m.setLayoutProperty(id, 'visibility', wanted ? 'visible' : 'none');
      // Which classes, and how strongly. Set here rather than only at layer creation, or the
      // chips and the slider would do nothing until the style happened to be rebuilt — which
      // is precisely the bug the airspace toggle itself had.
      m.setFilter(id, filter);
    }
    if (m.getLayer(`${AIRSPACE_SOURCE}-fill`) !== undefined) {
      m.setPaintProperty(`${AIRSPACE_SOURCE}-fill`, 'fill-extrusion-opacity', opacity);
    }
    if (m.getLayer(`${AIRSPACE_SOURCE}-line`) !== undefined) {
      // The outline carries the boundary, so it stays readable at low fill opacity rather than
      // fading with it — but it should not shout when the fill is off either.
      //
      // **Clamped**, and not defensively: 0.35 + 1.8 x 0.45 is 1.16, MapLibre rejects any paint
      // value above 1 outright, and the whole map went to "Karte nicht verfügbar" the first
      // time the slider passed a quarter.
      m.setPaintProperty(
        `${AIRSPACE_SOURCE}-line`,
        'line-opacity',
        Math.min(1, 0.35 + opacity * 1.8),
      );
    }
  });

  /**
   * The frame list, refreshed while the overlay is on.
   *
   * Only fetched once something asks for it: a page showing a flight from last week has no use
   * for it and should not be polling a weather service every few minutes to prove that.
   */
  $effect(() => {
    if (!offerRadar || !showRadar) return;
    const controller = new AbortController();

    const load = () => {
      api
        .radarFrames(controller.signal)
        .then((index) => {
          radarFrames = index.frames;
          // The newest *observation*, not the newest frame. A nowcast is a forecast, and
          // opening on one would show rain that has not happened as though it had.
          const observed = index.frames.filter((f) => !f.forecast);
          const newest = observed[observed.length - 1];
          if (newest !== undefined) radarFrame = newest.t;
        })
        .catch(() => {
          /* Overlay. The map is fine without it. */
        });
    };

    load();
    const timer = setInterval(load, 4 * 60_000);
    return () => {
      clearInterval(timer);
      controller.abort();
    };
  });

  /**
   * Thermal history for whatever is on screen, re-fetched when the view settles.
   *
   * On `moveend` rather than on `move`: a drag fires the latter dozens of times a second, and
   * the query behind this walks the fix table.
   */
  $effect(() => {
    const m = map;
    if (m === undefined || !ready || !offerThermals || !showThermals) return;
    const controller = new AbortController();

    const load = () => {
      const b = m.getBounds();
      api
        .thermals([b.getWest(), b.getSouth(), b.getEast(), b.getNorth()], controller.signal)
        .then((index) => {
          thermals = index;
        })
        .catch(() => {
          /* Overlay. */
        });
    };

    load();
    m.on('moveend', load);
    return () => {
      m.off('moveend', load);
      controller.abort();
    };
  });

  /** Draw them, or take them away. */
  $effect(() => {
    const m = map;
    const cells = showThermals && offerThermals ? (thermals?.cells ?? []) : [];
    if (m === undefined || !ready) return;

    const data: FeatureCollection = {
      type: 'FeatureCollection',
      features: cells.map((c) => ({
        type: 'Feature',
        properties: { climbMs: c.climbMs, bestMs: c.bestMs, samples: c.samples, altM: c.altM },
        geometry: { type: 'Point', coordinates: [c.lon, c.lat] },
      })),
    };

    if (m.getSource(THERMAL_SOURCE) === undefined) {
      m.addSource(THERMAL_SOURCE, { type: 'geojson', data });
      m.addLayer({
        id: THERMAL_SOURCE,
        type: 'circle',
        source: THERMAL_SOURCE,
        paint: {
          // Size is confidence — how often somebody climbed there — and colour is how well it
          // went. Two different questions, so two different channels; one blob scaled by a
          // single number would conflate "reliable" with "strong".
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['get', 'samples'],
            4,
            3,
            60,
            9,
          ],
          'circle-color': [
            'interpolate',
            ['linear'],
            ['get', 'climbMs'],
            0.5,
            '#4a90d9',
            1.5,
            '#5ad18f',
            3,
            '#f2c14e',
            5,
            '#ff7a3d',
          ],
          'circle-opacity': 0.75,
          'circle-stroke-color': 'rgba(0,0,0,0.5)',
          'circle-stroke-width': 0.8,
        },
      });
    } else {
      setData(m, THERMAL_SOURCE, data);
    }
  });

  /**
   * NOTAMs, as their published reference circles.
   *
   * Drawn as an outline rather than a filled area, deliberately. A filled shape reads as "this
   * is the affected airspace" and the data does not say that — degrees and whole minutes for
   * the centre, a whole number of nautical miles for the range. It is a marker saying "read
   * this one", which is what a NOTAM is.
   */
  $effect(() => {
    const m = map;
    const list = showNotams ? (notams?.notams ?? []) : [];
    if (m === undefined || !ready) return;

    const data: FeatureCollection = {
      type: 'FeatureCollection',
      features: list
        .filter((n) => n.lat !== null && n.lon !== null)
        .map((n) => ({
          type: 'Feature',
          properties: {
            number: n.number,
            label: n.location === null ? n.number : `${n.location} ${n.number}`,
            // One nautical mile is 1852 m; the radius is drawn in metres at the map's own
            // scale rather than in pixels, or a 5 NM circle would be the same size as a 50.
            radiusM: (n.radiusNm ?? 5) * 1852,
          },
          geometry: { type: 'Point', coordinates: [n.lon as number, n.lat as number] },
        })),
    };

    if (m.getSource(NOTAM_SOURCE) === undefined) {
      m.addSource(NOTAM_SOURCE, { type: 'geojson', data });
      m.addLayer({
        id: NOTAM_SOURCE,
        type: 'circle',
        source: NOTAM_SOURCE,
        paint: {
          // Metres to pixels: at zoom z one pixel is about 156543 * cos(lat) / 2^z metres, and
          // MapLibre has no metre-radius circle, so the conversion is done in the expression.
          'circle-radius': [
            'interpolate',
            ['exponential', 2],
            ['zoom'],
            6,
            ['/', ['get', 'radiusM'], 2445],
            14,
            ['/', ['get', 'radiusM'], 9.55],
          ],
          'circle-color': 'rgba(229, 84, 75, 0.07)',
          'circle-stroke-color': '#e5544b',
          'circle-stroke-width': 1.2,
          'circle-stroke-opacity': 0.8,
        },
      });
      m.addLayer({
        id: `${NOTAM_SOURCE}-label`,
        type: 'symbol',
        source: NOTAM_SOURCE,
        minzoom: 8,
        layout: {
          'text-field': ['get', 'label'],
          'text-font': ['Noto Sans Regular'],
          'text-size': 10,
          'text-allow-overlap': false,
        },
        paint: {
          'text-color': '#ffb3ae',
          'text-halo-color': 'rgba(0,0,0,0.8)',
          'text-halo-width': 1.4,
        },
      });
    } else {
      setData(m, NOTAM_SOURCE, data);
    }
  });

  /** The overlay itself: one raster source per frame, swapped when the frame changes. */
  $effect(() => {
    const m = map;
    const frame = radarFrame;
    const wanted = showRadar && offerRadar && frame !== null;
    if (m === undefined || !ready) return;

    if (m.getLayer(RADAR_SOURCE) !== undefined) m.removeLayer(RADAR_SOURCE);
    if (m.getSource(RADAR_SOURCE) !== undefined) m.removeSource(RADAR_SOURCE);
    if (!wanted) return;

    m.addSource(RADAR_SOURCE, {
      type: 'raster',
      tiles: [api.radarTileUrl(frame)],
      tileSize: 256,
      // The composite stops here; asking for more just overzooms a blur out of the daemon.
      maxzoom: 10,
      attribution:
        'Niederschlagsradar <a href="https://www.rainviewer.com/" target="_blank">RainViewer</a>',
    });
    // Under the track and the airspace — the point is what the aircraft is flying into, so the
    // aircraft has to stay on top of it.
    const below = m.getLayer('flight-ground') !== undefined ? 'flight-ground' : undefined;
    m.addLayer(
      {
        id: RADAR_SOURCE,
        type: 'raster',
        source: RADAR_SOURCE,
        paint: { 'raster-opacity': 0.6, 'raster-fade-duration': 200 },
      },
      below,
    );
  });

  /** The weather arrives after the map does, so the sky has to be re-derived when it lands. */
  $effect(() => {
    const m = map;
    void weather;
    if (m === undefined || !ready) return;
    skyPitch = -1;
    applySky(m);
  });

  export function flyToCursor(): void {
    const m = map;
    const point = track.geometry.coordinates[cursor];
    if (m === undefined || point === undefined) return;
    m.easeTo({ center: [point[0], point[1]], duration: 600 });
  }

  export function resetView(): void {
    map?.fitBounds(bounds(), { padding: 48, pitch: 62, bearing: -20, duration: 800 });
  }
</script>

<div class="map-wrap">
  <div class="map" bind:this={container}></div>

  {#if !ready && failure === null && !stalled}
    <div class="overlay">
      <LoopingPlane width={120} seconds={4} label="Lädt" />
      <span>Karte lädt …</span>
    </div>
  {/if}

  {#if stalled && failure === null}
    <div class="overlay error">
      Die Karte rendert nicht.
      <p class="faint">
        MapLibre zeichnet über <code>requestAnimationFrame</code>. Wenn der Tab im Hintergrund
        liegt oder der Browser die Seite nicht darstellt, läuft die Render-Schleife nicht — die
        Karte bleibt dann leer, obwohl alles geladen ist. Tab in den Vordergrund holen und neu
        laden. Bleibt es leer: WebGL prüfen und die Konsole ansehen.
      </p>
    </div>
  {/if}

  {#if failure !== null}
    <div class="overlay error">
      Karte nicht verfügbar: {failure}
      <p class="faint">
        Die Kacheln laufen über den Daemon. Ohne Internet-Egress im Pod bleibt die Karte leer —
        das Flugbuch selbst funktioniert weiter.
      </p>
    </div>
  {/if}

  <div class="topleft">
    <div class="switcher">
    <button
      class:active={basemap === 'satellite'}
      onclick={() => (basemap = 'satellite')}
      type="button">Satellit</button
    >
    <button class:active={basemap === 'vector'} onclick={() => (basemap = 'vector')} type="button"
      >Karte</button
    >
    {#if hasLayer('airspace')}
      <button class:active={showAirspace} onclick={() => (showAirspace = !showAirspace)} type="button"
        >Lufträume</button
      >
    {:else}
      <button
        class="disabled"
        type="button"
        disabled
        title="Braucht einen openAIP-API-Key (kostenlos auf accounts.openaip.net) in OPENAIP_API_KEY"
        >Lufträume</button
      >
    {/if}
    {#if offerRadar}
      <button class:active={showRadar} onclick={() => (showRadar = !showRadar)} type="button"
        >Regen</button
      >
    {/if}
    {#if notams?.configured === true}
      <button
        class:active={showNotams}
        onclick={() => (showNotams = !showNotams)}
        type="button"
        title="NOTAMs in der Nähe — der Kreis ist Bezugspunkt und Reichweite, nicht die betroffene Fläche"
        >NOTAM</button
      >
    {/if}
    {#if offerThermals}
      <button
        class:active={showThermals}
        onclick={() => (showThermals = !showThermals)}
        type="button"
        title="Wo diese Vereinsflugzeuge schon einmal gekreist und gestiegen sind — Historie, keine Vorhersage"
        >Thermik</button
      >
    {/if}
    </div>

    {#if showNotams && notams !== null}
      <div class="radar-bar notam-list">
        {#if !notams.available}
          <span class="faint">NOTAM-Dienst antwortet nicht.</span>
        {:else if notams.notams.length === 0}
          <span class="faint">Keine NOTAMs im Umkreis von {notams.centre.radiusNm} NM.</span>
        {:else}
          <div class="notam-scroll">
            <p class="faint caveat">
              {notams.notams.length} NOTAMs · Kreis = Bezugspunkt und Reichweite, nicht die
              betroffene Fläche. Maßgeblich ist der Text.
            </p>
            {#each notams.notams.slice(0, 25) as n (n.number)}
              <details>
                <summary>
                  <strong>{n.location ?? '—'} {n.number}</strong>
                  {#if n.radiusNm !== null}<span class="faint">{n.radiusNm} NM</span>{/if}
                </summary>
                <p>{n.text}</p>
              </details>
            {/each}
          </div>
        {/if}
      </div>
    {/if}

    {#if showAirspace && hasLayer('airspace')}
      <AirspaceControls
        bind:groups={airGroups}
        bind:opacity={airOpacity}
        variant="panel"
        hint="Höhen als Volumen: GND-Untergrenzen exakt, Flugflächen relativ zur Startplatzhöhe."
      />
    {/if}

    {#if offerThermals && showThermals}
      <div class="radar-bar thermal-note">
        {#if thermals === null}
          <span class="faint">Thermik lädt …</span>
        {:else if thermals.cells.length === 0}
          <span class="faint">Hier ist noch niemand aus dem Verein gekreist.</span>
        {:else}
          <span class="faint">
            {thermals.cells.length} Stellen · Punktgröße = wie oft, Farbe = wie stark
          </span>
        {/if}
      </div>
    {/if}

    {#if offerRadar && showRadar}
      <div class="radar-bar">
        {#if radarFrame === null}
          <span class="faint">Radar lädt …</span>
        {:else}
          <label>
            <span class="faint">Radar</span>
            <input
              type="range"
              min="0"
              max={Math.max(0, radarFrames.length - 1)}
              value={radarFrames.findIndex((f) => f.t === radarFrame)}
              oninput={(e) => {
                const f = radarFrames[Number((e.currentTarget as HTMLInputElement).value)];
                if (f !== undefined) radarFrame = f.t;
              }}
            />
          </label>
          <span class="stamp num">
            {new Date(radarFrame * 1000).toLocaleTimeString('de-DE', {
              hour: '2-digit',
              minute: '2-digit',
            })}
            {#if radarFrames.find((f) => f.t === radarFrame)?.forecast}
              <span class="fc">Vorhersage</span>
            {/if}
          </span>
        {/if}
      </div>
    {/if}

    {#if chrome !== undefined}
      {@render chrome()}
    {/if}
  </div>
</div>

<style>
  .map-wrap {
    position: relative;
    width: 100%;
    height: 100%;
    background: var(--bg-sunken);
  }

  .map {
    position: absolute;
    inset: 0;
  }

  .overlay {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.6rem;
    padding: 1.5rem;
    text-align: center;
    color: var(--text-dim);
    background: color-mix(in srgb, var(--bg-sunken) 90%, transparent);
    pointer-events: none;
  }
  .overlay.error {
    color: var(--warn);
  }
  .overlay p {
    margin: 0;
    max-width: 30rem;
    font-size: 0.85rem;
  }

  /* One column at the top left: the layer switcher and whatever the page stacks under it. The
     map owns the position, the page owns the contents. */
  .topleft {
    position: absolute;
    top: 0.6rem;
    left: 0.6rem;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.4rem;
    /* Above the canvas, below MapLibre's own controls' popups. */
    z-index: 2;
  }

  /* Same pill language as the switcher above it, and the same width, so the two read as one
     stack rather than two floating controls. */
  .radar-bar {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.3rem 0.6rem;
    border-radius: var(--radius-sm);
    background: color-mix(in srgb, var(--bg-raised) 88%, transparent);
    border: 1px solid var(--line);
    backdrop-filter: blur(8px);
    font-size: 0.75rem;
  }
  .radar-bar label {
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }
  .radar-bar input[type='range'] {
    width: 8rem;
    accent-color: var(--accent);
  }
  .radar-bar .stamp {
    font-variant-numeric: tabular-nums;
  }
  .notam-list {
    display: block;
    max-width: 24rem;
  }
  .notam-scroll {
    max-height: 14rem;
    overflow-y: auto;
  }
  .notam-list .caveat {
    margin: 0 0 0.4rem;
    line-height: 1.35;
  }
  .notam-list details {
    border-top: 1px solid var(--line);
    padding: 0.25rem 0;
  }
  .notam-list summary {
    cursor: pointer;
    font-size: 0.72rem;
    display: flex;
    gap: 0.4rem;
    align-items: baseline;
  }
  .notam-list details p {
    margin: 0.3rem 0 0.1rem;
    font-size: 0.7rem;
    line-height: 1.4;
    color: var(--text-dim);
    white-space: pre-wrap;
  }

  .thermal-note {
    max-width: 22rem;
    line-height: 1.35;
  }
  .radar-bar .fc {
    color: var(--accent);
    font-size: 0.66rem;
    margin-left: 0.2rem;
  }

  .switcher {
    display: flex;
    gap: 0.3rem;
    padding: 0.25rem;
    border-radius: var(--radius-sm);
    background: color-mix(in srgb, var(--bg-raised) 88%, transparent);
    border: 1px solid var(--line);
    backdrop-filter: blur(8px);
  }

  .switcher button {
    min-height: 34px;
    padding: 0 0.7rem;
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--text-dim);
  }
  .switcher button.active {
    background: var(--accent-soft);
    color: var(--accent);
  }
  .switcher button.disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
</style>
